import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { CircleNotch as Loader2, CheckCircle, WarningCircle as AlertCircle } from '@phosphor-icons/react';
import { ProgressIndicator } from './ProgressIndicator';
import { ServiceSelector } from './ServiceSelector';
import { TimeSlotPicker } from './TimeSlotPicker';
import { AppointmentSummary } from './AppointmentSummary';
import { useService } from '@/context/ServiceContext';
import {
  checkCustomerByCRM,
  getAvailableTimeSlots,
  createAppointment
} from '@/services/serviceApi';
import './AppointmentForm.css';

const STEPS = {
  IDENTIFICATION: 'identification',
  VEHICLE: 'vehicle',
  SERVICE: 'service',
  DATETIME: 'datetime',
  CONFIRMATION: 'confirmation',
  SUCCESS: 'success'
};

export function AppointmentForm() {
  const navigate = useNavigate();
  const { serviceCatalog, getServiceById } = useService();

  // Form state
  const [currentStep, setCurrentStep] = useState(STEPS.IDENTIFICATION);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [formData, setFormData] = useState({});
  const [customerFound, setCustomerFound] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [appointmentResult, setAppointmentResult] = useState(null);

  const updateFormData = (data) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  const markStepCompleted = (step) => {
    if (!completedSteps.includes(step)) {
      setCompletedSteps(prev => [...prev, step]);
    }
  };

  // Step 1: Identification
  const handleIdentificationSubmit = async (e) => {
    e.preventDefault();
    const licensePlate = e.target.licensePlate.value.trim().toUpperCase();
    const phone = e.target.phone.value.trim();

    setLoading(true);
    setError(null);

    try {
      const customer = await checkCustomerByCRM(licensePlate, phone);

      if (customer) {
        // Customer found
        setCustomerFound(true);
        updateFormData({
          ...customer,
          licensePlate,
          phone
        });
        markStepCompleted(STEPS.IDENTIFICATION);
        setCurrentStep(STEPS.SERVICE); // Skip vehicle info
      } else {
        // New customer
        setCustomerFound(false);
        updateFormData({ licensePlate, phone });
        markStepCompleted(STEPS.IDENTIFICATION);
        setCurrentStep(STEPS.VEHICLE);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Vehicle Info (new customers only)
  const handleVehicleSubmit = (e) => {
    e.preventDefault();
    updateFormData({
      customerName: e.target.customerName.value.trim(),
      carMake: e.target.carMake.value.trim(),
      carModel: e.target.carModel.value.trim(),
      carYear: parseInt(e.target.carYear.value),
      currentMileage: parseInt(e.target.currentMileage.value)
    });
    markStepCompleted(STEPS.VEHICLE);
    setCurrentStep(STEPS.SERVICE);
  };

  // Step 3: Service Selection
  const handleServiceSelect = (serviceId) => {
    const service = getServiceById(serviceId);
    updateFormData({
      serviceId: service.serviceId,
      serviceName: service.serviceName,
      standardPrice: service.standardPrice,
      requiredHours: service.requiredHours
    });
  };

  const handleServiceNext = () => {
    if (!formData.serviceId) {
      setError('Please select a service');
      return;
    }
    markStepCompleted(STEPS.SERVICE);
    setCurrentStep(STEPS.DATETIME);
  };

  // Step 4: Date & Time Selection
  const handleDateChange = async (e) => {
    const date = e.target.value;
    updateFormData({ appointmentDate: date, appointmentTime: null });
    setAvailableSlots([]);

    if (date && formData.requiredHours) {
      setSlotsLoading(true);
      try {
        const result = await getAvailableTimeSlots(date, formData.requiredHours);
        setAvailableSlots(result.slots);
      } catch (err) {
        setError('Failed to load available slots');
      } finally {
        setSlotsLoading(false);
      }
    }
  };

  const handleTimeSelect = (time) => {
    updateFormData({ appointmentTime: time });
  };

  const handleDateTimeNext = () => {
    if (!formData.appointmentDate || !formData.appointmentTime) {
      setError('Please select both date and time');
      return;
    }
    setError(null);
    markStepCompleted(STEPS.DATETIME);
    setCurrentStep(STEPS.CONFIRMATION);
  };

  // Step 5: Confirmation & Submit
  const handleFinalSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await createAppointment({
        customerId: formData.customerId || 'new',
        customerName: formData.customerName || formData.name,
        phone: formData.phone,
        licensePlate: formData.licensePlate,
        carMake: formData.carMake,
        carModel: formData.carModel,
        carYear: formData.carYear,
        serviceId: formData.serviceId,
        serviceName: formData.serviceName,
        appointmentDate: formData.appointmentDate,
        appointmentTime: formData.appointmentTime,
        durationHours: formData.requiredHours
      });

      setAppointmentResult(result);
      markStepCompleted(STEPS.CONFIRMATION);
      setCurrentStep(STEPS.SUCCESS);

      // Show browser control notification if available
      if (window.showSuccessBookingNotification) {
        window.showSuccessBookingNotification({
          type: 'service_booking',
          title: 'Service appointment confirmed',
          message: `${formData.serviceName} scheduled for ${formData.appointmentDate} at ${formData.appointmentTime}`,
          bookingId: result.appointmentId,
          details: {
            customerName: formData.customerName || formData.name,
            phone: formData.phone,
            serviceName: formData.serviceName,
            date: formData.appointmentDate,
            time: formData.appointmentTime
          }
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Navigation
  const handleBack = () => {
    const stepOrder = [STEPS.IDENTIFICATION, customerFound ? STEPS.SERVICE : STEPS.VEHICLE, STEPS.SERVICE, STEPS.DATETIME, STEPS.CONFIRMATION];
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(stepOrder[currentIndex - 1]);
    }
  };

  // Render current step
  const renderStep = () => {
    // Step 1: Identification
    if (currentStep === STEPS.IDENTIFICATION) {
      return (
        <Card className="appointment-form__card">
          <h2 className="appointment-form__heading appointment-form__heading--spaced">Your information</h2>
          <form onSubmit={handleIdentificationSubmit} className="appointment-form__stack">
            <div>
              <label className="appointment-form__field">License Plate Number *</label>
              <Input
                name="licensePlate"
                placeholder="XX-XXX-XX"
                pattern="[A-Z0-9-]+"
                required
                className="appointment-form__plate"
                defaultValue={formData.licensePlate}
              />
            </div>
            <div>
              <label className="appointment-form__field">Phone Number *</label>
              <Input
                name="phone"
                type="tel"
                placeholder="+31 6 12345678"
                required
                defaultValue={formData.phone}
              />
            </div>
            <Button type="submit" className="appointment-form__full" disabled={loading}>
              {loading ? <Loader2 className="appointment-form__spinner" /> : 'Continue'}
            </Button>
          </form>
        </Card>
      );
    }

    // Step 2: Vehicle Info (new customers only)
    if (currentStep === STEPS.VEHICLE) {
      return (
        <Card className="appointment-form__card">
          <h2 className="appointment-form__heading">Tell us about your vehicle</h2>
          <p className="appointment-form__lead">We'll save this information for future visits</p>
          <form onSubmit={handleVehicleSubmit} className="appointment-form__stack">
            <div>
              <label className="appointment-form__field">Your Name *</label>
              <Input name="customerName" required defaultValue={formData.customerName} />
            </div>
            <div>
              <label className="appointment-form__field">Car Make *</label>
              <Input name="carMake" placeholder="BMW, Audi, etc." required defaultValue={formData.carMake} />
            </div>
            <div>
              <label className="appointment-form__field">Car Model *</label>
              <Input name="carModel" placeholder="X5, A4, etc." required defaultValue={formData.carModel} />
            </div>
            <div className="appointment-form__pair">
              <div>
                <label className="appointment-form__field">Year *</label>
                <Input
                  name="carYear"
                  type="number"
                  min="1990"
                  max="2025"
                  required
                  defaultValue={formData.carYear}
                />
              </div>
              <div>
                <label className="appointment-form__field">Current Mileage (km) *</label>
                <Input
                  name="currentMileage"
                  type="number"
                  min="0"
                  required
                  defaultValue={formData.currentMileage}
                />
              </div>
            </div>
            <div className="appointment-form__row">
              <Button type="button" variant="outline" onClick={handleBack} className="appointment-form__grow">
                Back
              </Button>
              <Button type="submit" className="appointment-form__grow">Continue</Button>
            </div>
          </form>
        </Card>
      );
    }

    // Step 3: Service Selection
    if (currentStep === STEPS.SERVICE) {
      return (
        <Card className="appointment-form__card">
          <h2 className="appointment-form__heading">What service do you need?</h2>
          {customerFound && formData.lastVisitDate && (
            <p className="appointment-form__lead appointment-form__lead--sm">
              Last visit: {formData.lastVisitDate} - {formData.lastServiceType}
            </p>
          )}
          <div className="appointment-form__section">
            <ServiceSelector
              services={serviceCatalog}
              selectedServiceId={formData.serviceId}
              onSelect={handleServiceSelect}
            />
          </div>
          <div className="appointment-form__row">
            <Button variant="outline" onClick={handleBack} className="appointment-form__grow">
              Back
            </Button>
            <Button onClick={handleServiceNext} className="appointment-form__grow" disabled={!formData.serviceId}>
              Continue
            </Button>
          </div>
        </Card>
      );
    }

    // Step 4: Date & Time
    if (currentStep === STEPS.DATETIME) {
      const minDate = new Date();
      minDate.setDate(minDate.getDate() + 1); // Tomorrow
      const minDateStr = minDate.toISOString().split('T')[0];

      return (
        <Card className="appointment-form__card">
          <h2 className="appointment-form__heading">Choose date and time</h2>
          <p className="appointment-form__lead appointment-form__lead--sm">
            Please arrive 10 minutes before your appointment
          </p>

          <div className="appointment-form__stack appointment-form__stack--lg">
            <div>
              <label className="appointment-form__field">Select date</label>
              <Input
                type="date"
                min={minDateStr}
                value={formData.appointmentDate || ''}
                onChange={handleDateChange}
                required
              />
            </div>

            {formData.appointmentDate && (
              <div>
                <label className="appointment-form__field">Select time</label>
                <TimeSlotPicker
                  slots={availableSlots}
                  selectedTime={formData.appointmentTime}
                  onSelectTime={handleTimeSelect}
                  loading={slotsLoading}
                />
              </div>
            )}
          </div>

          <div className="appointment-form__row appointment-form__row--spaced">
            <Button variant="outline" onClick={handleBack} className="appointment-form__grow">
              Back
            </Button>
            <Button
              onClick={handleDateTimeNext}
              className="appointment-form__grow"
              disabled={!formData.appointmentDate || !formData.appointmentTime}
            >
              Continue
            </Button>
          </div>
        </Card>
      );
    }

    // Step 5: Confirmation
    if (currentStep === STEPS.CONFIRMATION) {
      return (
        <Card className="appointment-form__card">
          <h2 className="appointment-form__heading appointment-form__heading--spaced">Confirm your appointment</h2>

          <AppointmentSummary appointmentData={formData} />

          <div className="appointment-form__alert appointment-form__alert--info">
            <p className="appointment-form__alert-text">
              <strong>Note:</strong> You will receive a WhatsApp confirmation shortly.
              Please arrive 10 minutes before your scheduled time.
            </p>
          </div>

          <div className="appointment-form__row appointment-form__row--spaced">
            <Button variant="outline" onClick={handleBack} className="appointment-form__grow">
              Back
            </Button>
            <Button onClick={handleFinalSubmit} className="appointment-form__grow" disabled={loading}>
              {loading ? <Loader2 className="appointment-form__spinner" /> : 'Confirm Appointment'}
            </Button>
          </div>
        </Card>
      );
    }

    // Step 6: Success
    if (currentStep === STEPS.SUCCESS) {
      return (
        <Card className="appointment-form__card appointment-form__card--centered">
          <CheckCircle className="appointment-form__done-icon" />
          <h2 className="appointment-form__heading appointment-form__heading--lg">Appointment confirmed</h2>
          <p className="appointment-form__lead appointment-form__lead--xl">Booking ID: {appointmentResult?.appointmentId}</p>

          <div className="appointment-form__recap">
            <div className="appointment-form__stack appointment-form__stack--sm">
              <p className="appointment-form__inline">
                <span className="appointment-form__heading">📅</span>
                <span>{formData.appointmentDate}</span>
              </p>
              <p className="appointment-form__inline">
                <span className="appointment-form__heading">🕐</span>
                <span>{formData.appointmentTime}</span>
              </p>
              <p className="appointment-form__inline">
                <span className="appointment-form__heading">🔧</span>
                <span>{formData.serviceName}</span>
              </p>
              <p className="appointment-form__inline">
                <span className="appointment-form__heading">🚗</span>
                <span>{formData.carYear} {formData.carMake} {formData.carModel}</span>
              </p>
            </div>
          </div>

          <div className="appointment-form__alert appointment-form__alert--success">
            <p className="appointment-form__alert-text">
              📱 You will receive a WhatsApp confirmation shortly
            </p>
          </div>

          <div className="appointment-form__row appointment-form__row--responsive">
            <Button onClick={() => navigate('/service')} variant="outline" className="appointment-form__grow">
              Back to Services
            </Button>
            <Button onClick={() => navigate('/')} className="appointment-form__grow">
              Go Home
            </Button>
          </div>
        </Card>
      );
    }
  };

  return (
    <div className="appointment-form">
      {currentStep !== STEPS.SUCCESS && (
        <ProgressIndicator currentStep={currentStep} completedSteps={completedSteps} />
      )}

      {error && (
        <div className="appointment-form__alert appointment-form__alert--error">
          <AlertCircle className="appointment-form__alert-icon" />
          <div className="appointment-form__grow">
            <p className="appointment-form__alert-text">{error}</p>
          </div>
        </div>
      )}

      {renderStep()}
    </div>
  );
}
